import React, { FC, useState } from "react";
import Table from "antd/es/table";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import {
  useConfirmDeleteModal,
  useDebouncedTableFilter,
  useGetMachinesTableColumns,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import { handleRequestError } from "shared/helpers";
import {
  useDeleteMachineMutation,
  useGetAllMachinesQuery,
  MachineItem,
  ProcessTemplateItem,
} from "shared/api";
import { AddMachinesValues } from "shared/types";
import { expandMachinesString } from "shared/constants";

interface MachinesTableProps {
  showEditMachineModal: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  processTemplates: ProcessTemplateItem[];
}

const MachinesTable: FC<MachinesTableProps> = ({
  showEditMachineModal,
  hasDeletePermission,
  hasEditPermission,
  processTemplates,
}) => {
  const { t } = useTranslation();

  const { setValues } = useFormikContext<AddMachinesValues>();

  const pagination = usePagination();
  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    pagination;

  const { ordering, changeSort } = useTableSorting();

  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);

  const {
    debouncedValue: debouncedMachineNameValue,
    handleFilterChange: handleMachineNameValue,
    value: machineNameValue,
    clearFilterValue: clearMachineNameValue,
  } = useDebouncedTableFilter("MachineName", 400);

  const handleTemplateFilterChange = (values: number[]) => {
    setSelectedTemplates(values);
  };

  const {
    data: machines,
    isLoading: isLoadingProcessTypes,
    isFetching: isFetchingProcessTypes,
  } = useGetAllMachinesQuery({
    offset,
    limit,
    expand: expandMachinesString,
    name__icontains: debouncedMachineNameValue || undefined,
    process_template__in: selectedTemplates.length
      ? selectedTemplates
      : undefined,
    ordering,
  });

  const [deleteMachine] = useDeleteMachineMutation();
  const [api, contextHolder] = notification.useNotification();

  const deleteMachineHandler = async (record: MachineItem) => {
    try {
      await deleteMachine(record).unwrap();
      api.success({
        message: t("machines.success-title"),
        description: t("machines.success-delete-msg"),
      });
    } catch (error) {
      const description = handleRequestError(error, t("machines.error"));

      api.error({
        message: t("machines.error"),
        description,
      });
    }
  };

  const showMachineDeleteModal = useConfirmDeleteModal(
    t("machines.delete-alert-msg"),
    deleteMachineHandler
  );

  const editMachinesHandler = (record: MachineItem) => {
    showEditMachineModal();
    setValues({
      id: record?.id,
      name: record?.name,
      process_template: record.process_template.id,
    });
  };

  const columns = useGetMachinesTableColumns({
    t,
    editMachinesHandler,
    showMachineDeleteModal,
    changeSort,
    hasDeletePermission,
    hasEditPermission,
    handleMachineNameValue,
    machineNameValue,
    clearMachineNameValue,
    processTemplates,
    selectedTemplates,
    handleTemplateFilterChange,
  });

  return (
    <>
      {contextHolder}
      <Table
        loading={isLoadingProcessTypes || isFetchingProcessTypes}
        columns={columns}
        dataSource={machines?.results}
        rowKey={(obj) => obj.id!}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: machines?.count,
        }}
      />
    </>
  );
};

export { MachinesTable };
